import { useQueryClient } from '@tanstack/react-query';

import CapabilityCard from './capability-card';
import SettingsPanel from './settings-panel';
import Button from '../../../common/components/button/button';
import PageHeader from '../../../common/components/page-header/page-header';
import Section from '../../../common/components/section/section';
import ErrorState from '../../../common/components/state-message/error-state';
import LoadingState from '../../../common/components/state-message/loading-state';
import BellIcon from '../../../common/icons/bell';
import FingerprintIcon from '../../../common/icons/fingerprint';
import HapticIcon from '../../../common/icons/haptic';
import ShareIcon from '../../../common/icons/share';
import { useDevice } from '../../../global/contexts/device/device';
import { deviceKeys, useCapabilities, useNotificationPermission } from '../contexts/device';
import { useBridgeAction } from '../hooks/useBridgeAction';
import { describeHost, PERMISSION_LABEL, BIOMETRY_LABEL } from '../utils/labels';

import '../styles/overview.css';

const BIOMETRIC_REASON = 'Confirma que eres tú para probar el desbloqueo local';

export default function DeviceOverview() {
    const { bridge, embed } = useDevice();
    const queryClient = useQueryClient();
    const capabilitiesQuery = useCapabilities();
    const permissionQuery = useNotificationPermission();
    const shareAction = useBridgeAction();
    const hapticAction = useBridgeAction();
    const notifyAction = useBridgeAction();
    const permissionAction = useBridgeAction();
    const biometricAction = useBridgeAction();

    const result = capabilitiesQuery.data;
    const capabilities = result?.ok ? result.value : null;
    const permission = permissionQuery.data?.ok ? permissionQuery.data.value.state : 'unsupported';

    const requestPermission = async () => {
        await permissionAction.run((signal) => bridge.requestNotificationPermission({ signal }));
        queryClient.invalidateQueries({ queryKey: deviceKeys.notificationPermission(bridge.host) });
    };

    return (
        <div className="device-overview">
            <PageHeader
                eyebrow="Ajustes"
                title="Dispositivo"
                description="Capacidades reales que expone este host. Cada prueba se ejecuta solo cuando la pulsas; nada pide permisos al abrir la app."
            />

            <section className="device-overview__host" aria-label="Host actual">
                <p className="device-overview__host-label">Ejecutándose en</p>
                <p className="device-overview__host-value">{describeHost(bridge.host, capabilities?.platform)}</p>
                <p className="device-overview__host-meta">
                    Bridge <span className="mono">v{bridge.version}</span>
                    {embed.isEmbed && embed.hostOrigin && <> · host <span className="mono">{embed.hostOrigin}</span></>}
                </p>
                {embed.isEmbed && !embed.hostOrigin && (
                    <p className="device-overview__warning">
                        Modo embebido sin host de confianza ({embed.rejectionReason}). El puente con la página anfitriona está desactivado.
                    </p>
                )}
            </section>

            <Section title="Capacidades">
                {capabilitiesQuery.isPending && <LoadingState label="Detectando capacidades" rows={2} />}
                {capabilitiesQuery.isError && <ErrorState error={capabilitiesQuery.error} onRetry={capabilitiesQuery.refetch} />}
                {result && !result.ok && (
                    <p className="device-overview__warning" role="alert">
                        No se pudieron detectar capacidades: {result.error.message} <code>{result.error.code}</code>
                    </p>
                )}
                {capabilities && (
                    <ul className="device-overview__grid">
                        <CapabilityCard
                            icon={<ShareIcon />}
                            title="Compartir"
                            description="Abre la hoja de compartir del sistema con un texto de prueba."
                            isAvailable={capabilities.share}
                            action={shareAction}
                            successText="El sistema confirmó que se compartió."
                        >
                            {capabilities.share && (
                                <Button
                                    isBusy={shareAction.isPending}
                                    onClick={() => shareAction.run((signal) => bridge.share(
                                        { title: 'Mastermind', text: 'Prueba de compartir desde el borrador móvil de Mastermind.' },
                                        { signal },
                                    ))}
                                >
                                    Probar compartir
                                </Button>
                            )}
                        </CapabilityCard>

                        <CapabilityCard
                            icon={<HapticIcon />}
                            title="Háptica"
                            description={capabilities.haptics
                                ? 'Vibración breve del motor háptico.'
                                : 'Este host no ofrece háptica verificable (en navegador no se simula).'}
                            isAvailable={capabilities.haptics}
                            action={hapticAction}
                            successText="El sistema aceptó la orden. En equipos sin motor háptico no se percibe nada (Capacitor resuelve igual)."
                        >
                            {capabilities.haptics && (
                                <>
                                    <Button onClick={() => hapticAction.run((signal) => bridge.haptic({ style: 'medium' }, { signal }))}>Impacto</Button>
                                    <Button onClick={() => hapticAction.run((signal) => bridge.haptic({ style: 'success' }, { signal }))}>Éxito</Button>
                                </>
                            )}
                        </CapabilityCard>

                        <CapabilityCard
                            icon={<BellIcon />}
                            title="Notificación local"
                            description="Notificación generada en este dispositivo. No es push remoto: APNs/FCM y el registro en Core no existen todavía."
                            isAvailable={capabilities.localNotifications}
                            availabilityText={capabilities.localNotifications ? `Permiso: ${PERMISSION_LABEL[permission]}` : undefined}
                            action={permission === 'granted' ? notifyAction : permissionAction}
                            successText={permission === 'granted' ? 'Notificación entregada al sistema.' : `Permiso: ${PERMISSION_LABEL[permission]}.`}
                        >
                            {capabilities.localNotifications && permission === 'prompt' && (
                                <Button isBusy={permissionAction.isPending} onClick={requestPermission}>Pedir permiso</Button>
                            )}
                            {capabilities.localNotifications && permission === 'granted' && (
                                <Button
                                    isBusy={notifyAction.isPending}
                                    onClick={() => notifyAction.run((signal) => bridge.notifyLocal(
                                        { title: 'Mastermind', body: 'Notificación local de prueba (no es push).' },
                                        { signal },
                                    ))}
                                >
                                    Enviar notificación de prueba
                                </Button>
                            )}
                            {capabilities.localNotifications && permission === 'denied' && (
                                <p className="device-overview__hint">El permiso está denegado. Cámbialo desde los ajustes del sistema.</p>
                            )}
                        </CapabilityCard>

                        <CapabilityCard
                            icon={<FingerprintIcon />}
                            title="Biometría local"
                            description={capabilities.biometric.available
                                ? `${BIOMETRY_LABEL[capabilities.biometric.kind]}: comprueba que eres tú en este dispositivo. No inicia sesión en Mastermind ni autoriza operaciones de Core.`
                                : capabilities.biometric.reason || 'No disponible en este host.'}
                            isAvailable={capabilities.biometric.available}
                            availabilityText={capabilities.biometric.available ? BIOMETRY_LABEL[capabilities.biometric.kind] : undefined}
                            action={biometricAction}
                            successText="Identidad verificada localmente. Ningún dato biométrico salió del dispositivo y no se creó sesión."
                        >
                            {capabilities.biometric.available && (
                                <Button
                                    isBusy={biometricAction.isPending}
                                    onClick={() => biometricAction.run((signal) => bridge.authenticateBiometric({ reason: BIOMETRIC_REASON }, { signal }))}
                                >
                                    Verificar localmente
                                </Button>
                            )}
                            {biometricAction.isPending && <Button variant="ghost" onClick={biometricAction.cancel}>Cancelar</Button>}
                        </CapabilityCard>
                    </ul>
                )}
            </Section>

            <Section title="Push remoto">
                <div className="device-overview__pending">
                    <p><strong>Pendiente.</strong> Las notificaciones push requieren APNs (iOS), FCM (Android) y un endpoint de registro de dispositivos en Core que hoy no existe en el contrato.</p>
                    <p>Este borrador no simula entregas push. Consulta <span className="mono">docs/native-bridge.md</span> para el contrato propuesto.</p>
                </div>
            </Section>

            <SettingsPanel />
        </div>
    );
}
